import { differenceInCalendarDays, format, isValid, parse, parseISO, toDate } from 'date-fns'

import type { PackagesFormStateType } from '@/components/Packages/types'
import type { LOCATION_SEARCH_TYPE } from '@/types'
import type { ChildrenAges } from '@pcln/traveler-selection'

const MAX_CALENDAR_DAYS = 329

/** Aligns with pkg-search getState date handling (yyyyMMdd output, future window). */
export function sanitizePkgDateInput(date: string | undefined): string {
  if (!date) return ''
  const today = new Date()
  let parsed: Date
  if (/^\d{8}$/.test(date)) {
    parsed = parse(date, 'yyyyMMdd', today)
  } else {
    const isoTry = parseISO(date)
    parsed = isValid(isoTry) ? isoTry : parse(date, 'yyyy-MM-dd', today)
  }
  if (!isValid(parsed)) return ''
  const asYyyymmdd = format(parsed, 'yyyyMMdd')
  const diff = differenceInCalendarDays(parsed, toDate(today))
  return diff >= 0 && diff <= MAX_CALENDAR_DAYS ? asYyyymmdd : ''
}

/** Maps cart/Trips geo to vacationpackages typeahead shape (same as pkg-search buildVacationPackagesFormState). */
export function mapGeoToLandingLocation(
  geo:
    | {
        id?: string | number
        displayName?: string
        name?: string
        isoCountryCode?: string
        countryCode?: string
        type?: string
        countryName?: string
        country?: string
        latitude?: number
        longitude?: number
        stateName?: string
        stateCode?: string
      }
    | null
    | undefined,
): LOCATION_SEARCH_TYPE | null {
  if (!geo || (geo.id == null && !geo.displayName)) return null
  const id = geo.id != null ? String(geo.id) : ''
  const itemName = geo.displayName || geo.name || ''
  return {
    id,
    itemName,
    countryCode: geo.isoCountryCode || geo.countryCode || '',
    type: (typeof geo.type === 'string' ? geo.type : 'CITY') as LOCATION_SEARCH_TYPE['type'],
    displayName: geo.displayName,
    cityName: geo.displayName,
    country: geo.countryName || geo.country,
    countryName: geo.countryName,
    lat: geo.latitude,
    lon: geo.longitude,
    provinceName: geo.stateName,
    stateCode: geo.stateCode,
    content: geo.name,
  }
}

function tripTypeFromInitialValues(initialValues: Record<string, unknown>): string {
  const parts: string[] = []
  if (initialValues?.hasHotels) parts.push('STAY')
  if (initialValues?.hasFlights) parts.push('FLY')
  if (initialValues?.hasCars) parts.push('DRIVE')
  let tripType = parts.join(':')
  const allowed = new Set(['STAY:FLY', 'STAY:FLY:DRIVE', 'FLY:DRIVE', 'STAY:DRIVE'])
  if (!allowed.has(tripType)) {
    tripType = 'STAY:FLY'
  }
  return tripType
}

function childrenAgesArrayToObject(childrenAges: unknown): ChildrenAges {
  const out: ChildrenAges = {}
  if (!Array.isArray(childrenAges)) return out
  childrenAges.forEach((ageStr, idx) => {
    const age = Number(ageStr)
    if (!Number.isNaN(age)) {
      out[idx] = String(ageStr)
    }
  })
  return out
}

/**
 * Builds vacationpackages form state from Trips `initialValues` (GET /shop/search/getstate `initialValues` shape).
 * Ported from pkg-search `buildVacationPackagesFormState`.
 */
export function buildVacationPackagesFormFromTripsInitialValues(
  initialValues: Record<string, unknown>,
): PackagesFormStateType {
  const tripType = tripTypeFromInitialValues(initialValues)

  const hotels = (initialValues?.hotels as Array<{ startDate?: string; endDate?: string }> | undefined) ?? []
  const startDate = (initialValues?.startDate as string | undefined) ?? ''
  const endDate = (initialValues?.endDate as string | undefined) ?? ''

  const rawHotelStart = hotels[0]?.startDate
  const rawHotelEnd = hotels[0]?.endDate
  const hotelStartDate = rawHotelStart ? sanitizePkgDateInput(rawHotelStart) : startDate
  const hotelEndDate = rawHotelEnd ? sanitizePkgDateInput(rawHotelEnd) : endDate

  const partialStay = Boolean(
    startDate &&
    hotelStartDate &&
    endDate &&
    hotelEndDate &&
    (hotelStartDate !== startDate || hotelEndDate !== endDate),
  )

  const carPickUpLocation =
    typeof initialValues.carPickUpLocation === 'string' ? initialValues.carPickUpLocation.trim() : ''
  const endLocationCode =
    typeof initialValues.endLocation === 'string' ? initialValues.endLocation.trim() : ''

  const pickupCarSomewhere = Boolean(
    initialValues?.hasCars &&
    initialValues?.carSearchType === 'custom' &&
    carPickUpLocation &&
    endLocationCode &&
    carPickUpLocation !== endLocationCode,
  )

  const startLoc = mapGeoToLandingLocation(
    initialValues?.startLocationObject as Parameters<typeof mapGeoToLandingLocation>[0],
  )
  const endLoc = mapGeoToLandingLocation(
    initialValues?.endLocationObject as Parameters<typeof mapGeoToLandingLocation>[0],
  )

  const childrenAges = childrenAgesArrayToObject(initialValues?.childrenAges)

  const adults = (initialValues?.adultCount as number | undefined) ?? 2
  const roomCount = (initialValues?.roomCount as number | undefined) ?? 1

  const childrenCount =
    (initialValues?.childrenCount as number | undefined) ??
    (Array.isArray(initialValues?.childrenAges) ? (initialValues.childrenAges as unknown[]).length : 0)

  const rcLocation = endLoc

  return {
    tripType: tripType as PackagesFormStateType['tripType'],
    partialStay,
    startLocation: startLoc,
    endLocation: endLoc,
    startDate,
    endDate,
    hotelStartDate,
    hotelEndDate,
    travelers: {
      rooms: roomCount,
      adults,
      children: childrenCount,
      childrenAges,
      lapInfants: 0,
    },
    isRecommendedTrip: false,
    includeBasicEconomy: (initialValues?.includeBasicEconomy as boolean | undefined) ?? true,
    pickupCarSomewhere,
    rcLocation,
    rcPickupDate: (initialValues?.carPickUpDate as string | undefined) ?? hotelStartDate,
    rcDropoffDate: (initialValues?.carDropOffDate as string | undefined) ?? hotelEndDate,
    rcPickupTime: (initialValues?.carPickUpTime as string | undefined) ?? '10:00',
    rcDropoffTime: (initialValues?.carDropOffTime as string | undefined) ?? '10:00',
    isInfantOnInternationalTravel: undefined,
  }
}
